(function () {
    /*
      Define second layer events that should be tracked.
      Second layer events are always derived from first layer events.
      They are used to track more specific user interactions on the page.
      If you want to track a second layer event, just add a transform to the following array.
      Each transform must have:
      - event (string): the name of the first layer event
      - condition (function): a function that receives the first layer event data and returns a boolean indicating if a second layer event should be created
      - subevent (string|function): the suffix of the second layer event (second layer events are always in the format `event__suffix`), can be a string or a function that receives the first layer event data and returns a string
      Example:
      {
        event: 'event_name',
        condition: d => {
          return d.someProperty === 'someValue';
        },
        subevent: 'suffix'
      }
    */
    const secondLayerTransforms = [
        {
            event: 'page_viewed',
            condition: (d) => d.template.suffix && d.template.suffix.includes('sales-page'),
            subevent: 'sales_page'
        },
        {
            event: 'page_viewed',
            condition: (d) => d.template && d.template.suffix === 'sales-page-vsl',
            subevent: 'sales_page_vsl'
        },
        // Generic sections
        {
            event: 'section_viewed',
            condition: (d) => {
                return [
                    'offers',
                    'solution',
                    'customer-story',
                    'review-screenshots',
                    'bonuses',
                    'customer-service',
                    'dream-scenario',
                    'guarantee-and-certification',
                    'pain-points'
                ].includes(d.section.handle);
            },
            subevent: (d) => d.section.handle.replace(/-/g, '_')
        },
        // Sections on sales pages
        {
            event: 'section_viewed',
            condition: (d) => {
                return d.template.suffix &&
                    d.template.suffix.includes('sales-page') &&
                    d.section.handle === 'product-reviews';
            },
            subevent: 'sales_page_product_reviews'
        },
        {
            event: 'section_viewed',
            condition: (d) => {
                return d.template.suffix &&
                    d.template.suffix.includes('sales-page') &&
                    d.section.handle === 'faq';
            },
            subevent: 'sales_page_faq'
        },
        {
            event: 'video_progressed',
            condition: (d) => {
                // Move to session storage
                const player = document.querySelector(`wistia-player[media-id="${d.video.id}"]`);
                if (d.video.percentWatched >= 0.9 && player && !player.hasAttribute('data-tracking-video-completed')) {
                    player.setAttribute('data-tracking-video-completed', 'true');
                    return true;
                }
                return false;
            },
            subevent: 'completion'
        },
        {
            // Save to session storage
            event: 'video_progressed',
            condition: (d) => d.video.vslProgressBasedContentReached,
            subevent: 'vsl_progress_based_content'
        },
        {
            // Save to session storage
            event: 'video_progressed',
            condition: (d) => d.video.currentTime >= 60,
            subevent: 'one_minute_reached'
        },
        {
            event: 'video_started',
            condition: (d) => d.video.vsl,
            subevent: 'vsl'
        },
        {
            event: 'element_clicked',
            condition: (d) => {
                return (
                // Consider current setup with button instead of link for variant selections
                d.section && d.section.handle === 'offers' && (d.element.dataset.action === 'checkout' || d.element.url && d.element.url.pathname.includes('/cart'))
                /* For reference of actual definition
                d.element.type === 'link' &&
                d.section &&
                d.section.handle === 'offers' &&
                d.element.url &&
                d.element.url.href.includes('https://vitamoment.de/cart/')
                */
                );
            },
            subevent: 'offer_button'
        },
        {
            event: 'element_clicked',
            condition: (d) => d.element.type === 'link' && d.element.outbound,
            subevent: 'outbound_link'
        },
        {
            event: 'element_clicked',
            condition: (d) => d.element.type === 'link' && d.element.outbound && d.element.url.hostname === 'vitamoment.de',
            subevent: 'outbound_link_vm'
        },
        {
            event: 'element_clicked',
            condition: (d) => d.element.type === 'link' && d.element.outbound && d.element.url.hostname != 'vitamoment.de',
            subevent: 'outbound_link_non_vm'
        }
    ];
    /*
      Define events that should be sent to third-party services.
      All first and second layer events can be used, see user events documentation for info about available events and data.
      Add objects to the following array to define third-party events.
      Each object must have the following properties:
      - service (string): the name of the third-party service ('facebook', 'admetrics', 'kameleoon')
      - event (string): the name of the event to be sent to the third-party services
      Optionally, you can use the following properties:
      - seviceEvent (string): the name of the event to be sent to the third-party service, if not provided the event name will be used
      - serviceNative (boolean): if the service is facebook and this is true, the event will be handled as a native facebook event, otherwise it will be handled as a custom event
      - condition (function): a function that receives the event data and returns a boolean indicating if the event should be sent to the third-party service, if not provided the event will always be sent
      - data (function): a function that receives the event data and returns an object with the data to be sent to the third-party service, if not provided no data will be sent
      Example:
      {
        service: 'facebook',
        event: 'video_started',
        serviceEvent: 'VideoStarted',
        serviceNative: true,
        condition: d => { return d.video.vsl; },
        data: d => {
          return { videoId: d.video.id, videoTitle: d.video.title };
        }
      }
    */
    const thirdPartyEvents = [
        // {
        //   service: 'facebook',
        //   event: 'form_submitted',
        //   serviceEvent: 'CompleteRegistration',
        //   data: d => {
        //     const coachingHandle = d.form.elements.find(element => element.name === 'coaching_handle');
        //     return {
        //       content_name: d.form.name === 'Free Coaching Signup' && coachingHandle ? `${d.form.name} - ${coachingHandle}` : d.form.name
        //     };
        //   }
        // },
        // {
        //   service: 'facebook',
        //   event: 'section_viewed__offers',
        //   serviceEvent: 'ViewOffer'
        // },
        // {
        //   service: 'facebook',
        //   event: 'content_viewed',
        //   serviceEvent: 'ViewContent'
        // },
        {
            service: 'admetrics',
            event: 'content_viewed'
        },
        {
            service: 'admetrics',
            event: 'section_viewed__offers'
        },
        {
            service: 'admetrics',
            event: 'page_viewed__sales_page'
        },
        {
            service: 'kameleoon',
            event: 'content_viewed',
            serviceEvent: '385338'
        },
        {
            service: 'kameleoon',
            event: 'engaged',
            serviceEvent: '385337'
        },
        {
            service: 'kameleoon',
            event: 'page_viewed__sales_page',
            serviceEvent: '338462'
        },
        {
            service: 'kameleoon',
            event: 'page_viewed__sales_page_vsl',
            serviceEvent: '385341'
        },
        {
            service: 'kameleoon',
            event: 'section_viewed__offers',
            serviceEvent: '338461'
        },
        {
            service: 'kameleoon',
            event: 'section_viewed__dream_scenario',
            serviceEvent: '385343'
        },
        {
            service: 'kameleoon',
            event: 'section_viewed__customer_story',
            serviceEvent: '385345'
        },
        {
            service: 'kameleoon',
            event: 'section_viewed__guarantee_and_certification',
            serviceEvent: '385346'
        },
        {
            service: 'kameleoon',
            event: 'section_viewed__solution',
            serviceEvent: '385344'
        },
        {
            service: 'kameleoon',
            event: 'section_viewed__review_screenshots',
            serviceEvent: '385347'
        },
        {
            service: 'kameleoon',
            event: 'section_viewed__pain_points',
            serviceEvent: '385342'
        },
        {
            service: 'kameleoon',
            event: 'section_viewed__bonuses',
            serviceEvent: '385349'
        },
        {
            service: 'kameleoon',
            event: 'section_viewed__sales_page_faq',
            serviceEvent: '385351'
        },
        {
            service: 'kameleoon',
            event: 'section_viewed__sales_page_product_reviews',
            serviceEvent: '385350'
        },
        {
            service: 'kameleoon',
            event: 'video_started__vsl',
            serviceEvent: '359879'
        },
        {
            service: 'kameleoon',
            event: 'video_progressed__vsl_progress_based_content',
            serviceEvent: '385353'
        },
        {
            service: 'kameleoon',
            event: 'video_progressed__one_minute_reached',
            serviceEvent: '385354'
        },
        {
            service: 'kameleoon',
            event: 'video_progressed__completion',
            serviceEvent: '385352'
        },
        {
            service: 'kameleoon',
            event: 'element_clicked__offer_button',
            serviceEvent: '366118'
        },
        {
            service: 'kameleoon',
            event: 'element_clicked__outbound_link_vm',
            serviceEvent: '385355'
        },
        {
            service: 'kameleoon',
            event: 'element_clicked__outbound_link_non_vm',
            serviceEvent: '385356'
        }
    ];
    window.theme.tracking.secondLayerTransforms = secondLayerTransforms;
    window.theme.tracking.thirdPartyEvents = thirdPartyEvents;
})();
(function () {
    // Temporary slider tracking for a/b testing
    const productSlideshowElement = document.querySelector('.Product__Slideshow .Slider');
    if (!productSlideshowElement)
        return;
    const slideChangeListener = () => {
        let tries = 0;
        const processGoal = () => {
            // @ts-expect-error
            if (Kameleoon && Kameleoon.API && Kameleoon.API.Goals) {
                console.log('[A/B] Product image slider slide change goal reached');
                // @ts-expect-error
                Kameleoon.API.Goals.processConversion(368690);
            }
            else if (tries++ < 10) {
                setTimeout(processGoal, 1000);
            }
        };
        processGoal();
        productSlideshowElement.removeEventListener('theme:slider:slidechanged', slideChangeListener);
    };
    productSlideshowElement.addEventListener('theme:slider:slidechanged', slideChangeListener);
})();
export {};
